import { DefaultLoader } from '@euler/app/components/loaders/DefaultLoader';
import {
  TaskCardV2,
  useTaskCardV2Metrics,
} from '@euler/app/components/task-card';
import {
  ReportViewSummaryProvider,
  useCreateReportViewSummaryStore,
} from '@euler/app/components/task-card/report-view-summary/ReportViewSummaryContext';
import { useTrackVisibleOrders } from '@euler/app/components/task-card/report-view-summary/useTrackVisibleOrders';
import { FooterView } from '@euler/app/flows/home/components/FooterView';
import {
  TaskCard,
  useTaskCardMetrics,
} from '@euler/app/flows/home/components/TaskCard';
import { Center } from '@euler/components/Center';
import { EmptyView } from '@euler/components/EmptyView';
import { computeGridLayoutItemSize } from '@euler/functions/layoutMetrics';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import { TaskListItem } from '@euler/model/TaskListItem';
import { safeMarkDevFlag } from '@euler/utils';
import { useWatchValue } from '@euler/utils/hooks';
import {
  ReactNode,
  forwardRef,
  memo,
  useCallback,
  useImperativeHandle,
  useMemo,
  useRef,
} from 'react';
import {
  ActivityIndicator,
  FlatListProps,
  ListRenderItem,
  NativeScrollEvent,
  NativeSyntheticEvent,
  Platform,
  RefreshControl as RNRefreshControl,
  StyleProp,
  ViewStyle,
} from 'react-native';
import { FlatList, createNativeWrapper } from 'react-native-gesture-handler';
import { useSharedValue } from 'react-native-reanimated';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

const kMarginMultiColumn = 7.5;
const kMarginSingleColumn = 6.5;

const kFakeEmpty = safeMarkDevFlag(false);

const keyExtractor = (item: TaskListItem) => item.id;

// see https://github.com/software-mansion/react-native-gesture-handler/issues/1067
// and this pull request probably will solve this problem:
// https://github.com/software-mansion/react-native-gesture-handler/pull/2116/
const RefreshControl =
  Platform.OS === 'android'
    ? createNativeWrapper(RNRefreshControl, {
        disallowInterruption: true,
        shouldCancelWhenOutside: false,
      })
    : RNRefreshControl;

export type TaskListRef = {
  scrollToTop: (animated?: boolean) => void;
  getContentOffset: () => number;
};

type Props = {
  width: number;
  style?: StyleProp<ViewStyle>;
  columns?: number;
  data: TaskListItem[];
  isRefreshing?: boolean;
  isLoading?: boolean;
  error?: Error;
  hasMore?: boolean;
  showFinishDate?: boolean;
  hideStatusLabel?: boolean;
  extraBottomSpacing?: number;
  emptyText?: ReactNode;
  showRollbackDetail?: boolean;
  showCancelReason?: boolean;
  useV2Card?: boolean;
  onRetry?: () => void;
  onTaskPress?: (task: TaskListItem) => void;
  onRefresh?: () => void;
  onScrollToBottom?: () => void;
  onScrollOffsetChange?: (offset: number) => void;
} & Pick<
  FlatListProps<TaskListItem>,
  | 'ListFooterComponent'
  | 'ListHeaderComponent'
  | 'ListEmptyComponent'
  | 'contentContainerStyle'
>;

export const TaskList = memo(
  forwardRef<TaskListRef, Props>(
    (
      {
        width,
        style,
        data,
        columns,
        error,
        emptyText,
        isLoading,
        isRefreshing,
        hasMore,
        hideStatusLabel,
        showFinishDate,
        showRollbackDetail,
        showCancelReason,
        useV2Card,
        contentContainerStyle,
        extraBottomSpacing = 0,
        ListHeaderComponent,
        ListFooterComponent,
        ListEmptyComponent,
        onTaskPress,
        onRefresh,
        onScrollToBottom,
        onScrollOffsetChange,
        onRetry,
      },
      ref,
    ) => {
      const { rpx } = useResponsiveContext();
      const refreshRef = useRef(null);
      const listRef = useRef<FlatList<TaskListItem>>(null);

      const safeAreaInsets = useSafeAreaInsets();
      const scrollY = useSharedValue(0);

      useImperativeHandle(ref, () => ({
        scrollToTop: (animated?: boolean) => {
          listRef.current?.scrollToOffset({ animated, offset: 0 });
        },
        getContentOffset: () => scrollY.value,
      }));

      const onScroll = useCallback(
        (e: NativeSyntheticEvent<NativeScrollEvent>) => {
          scrollY.value = e.nativeEvent.contentOffset.y;
          onScrollOffsetChange?.(e.nativeEvent.contentOffset.y);
        },
        [onScrollOffsetChange, scrollY],
      );

      const multiColumn = columns != null && columns > 1;

      const [cellWidth] = computeGridLayoutItemSize({
        numberOfColumns: columns ?? 1,
        gridPadding: {
          left: rpx(10),
          right: rpx(10),
          top: rpx(0),
          bottom: rpx(0),
        },
        gridWidth: width,
        aspectRatio: 4 / 3,
        cellMargin: multiColumn
          ? {
              left: rpx(kMarginMultiColumn),
              right: rpx(kMarginMultiColumn),
              top: rpx(kMarginMultiColumn),
              bottom: rpx(kMarginMultiColumn),
            }
          : {
              left: rpx(kMarginSingleColumn),
              right: rpx(kMarginSingleColumn),
              top: rpx(kMarginSingleColumn),
              bottom: rpx(kMarginSingleColumn),
            },
      });

      // The report read/view status is fetched per order — the store keeps
      // that to the cards actually on screen, and refreshes them whenever the
      // screen regains focus (the status changes on the task detail page).
      const summaryStore = useCreateReportViewSummaryStore();
      const viewabilityConfigCallbackPairs = useTrackVisibleOrders(
        summaryStore,
        data,
        useV2Card === true,
      );

      // any refresh of the list (pull-to-refresh, the header button, a
      // segment/tag switch) invalidates the loaded statuses too.
      useWatchValue(isRefreshing, (prev, curr) => {
        // fire once the refresh settles, so the list data is already updated.
        if (prev && !curr) summaryStore.refreshVisible();
      });

      const { height: cellHeightV1 } = useTaskCardMetrics();
      const { height: cellHeightV2 } = useTaskCardV2Metrics();
      const cellHeight = useV2Card ? cellHeightV2 : cellHeightV1;

      const cellStyle = useMemo<ViewStyle>(
        () =>
          multiColumn
            ? {
                width: cellWidth,
                height: cellHeight,
                margin: rpx(kMarginMultiColumn),
              }
            : {
                width: cellWidth,
                margin: rpx(kMarginSingleColumn),
              },
        [multiColumn, cellWidth, cellHeight, rpx],
      );

      const renderItem: ListRenderItem<TaskListItem> = useCallback(
        ({ item }) =>
          useV2Card ? (
            <TaskCardV2
              item={item}
              style={cellStyle}
              hideStatusLabel={hideStatusLabel}
              onPress={onTaskPress}
            />
          ) : (
            <TaskCard
              item={item}
              style={cellStyle}
              width={cellWidth}
              showFinishDate={showFinishDate}
              hideStatusLabel={hideStatusLabel}
              showRollbackDetail={showRollbackDetail}
              showCancelReason={showCancelReason}
              onPress={onTaskPress}
            />
          ),
        [
          useV2Card,
          cellStyle,
          cellWidth,
          hideStatusLabel,
          onTaskPress,
          showFinishDate,
          showRollbackDetail,
          showCancelReason,
        ],
      );

      return (
        <ReportViewSummaryProvider value={summaryStore}>
          <FlatList
            ref={listRef}
            data={kFakeEmpty ? [] : data}
            keyExtractor={keyExtractor}
            style={style}
            waitFor={Platform.OS === 'android' ? refreshRef : undefined}
            key={`columns-${columns ?? 1}`}
            numColumns={columns}
            onScroll={onScroll}
            scrollEventThrottle={1}
            contentContainerStyle={[
              {
                flexGrow: 1,
                paddingBottom: extraBottomSpacing + safeAreaInsets.bottom,
                paddingLeft: rpx(10),
                paddingRight: rpx(10),
                // Cap and center the content rather than the FlatList itself
                // — that way the scrollable area still spans the full pane and
                // you can wheel-scroll from anywhere, not only over the grid.
                width: '100%',
                maxWidth: width,
                alignSelf: 'center',
              },
              contentContainerStyle,
            ]}
            refreshControl={
              Platform.OS === 'web' ? undefined : (
                <RefreshControl
                  ref={refreshRef}
                  refreshing={(isRefreshing && data.length > 0) ?? false}
                  onRefresh={onRefresh}
                />
              )
            }
            onEndReachedThreshold={0.3}
            onEndReached={onScrollToBottom}
            renderItem={renderItem}
            viewabilityConfigCallbackPairs={viewabilityConfigCallbackPairs}
            ListEmptyComponent={
              ListEmptyComponent ??
              ((isLoading && data.length === 0 && !isRefreshing) ||
              kFakeEmpty ? (
                <Center
                  css={`
                    flex: 1;
                  `}
                >
                  <DefaultLoader />
                </Center>
              ) : (
                <EmptyView
                  error={error}
                  onRetry={onRefresh}
                  message={emptyText}
                />
              ))
            }
            ListHeaderComponent={
              ListHeaderComponent ??
              (Platform.OS === 'web' && isRefreshing && data.length > 0 ? (
                <Center
                  css={`
                    padding: ${rpx(15)}px;
                  `}
                >
                  <ActivityIndicator />
                </Center>
              ) : null)
            }
            ListFooterComponent={
              ListFooterComponent ??
              (!kFakeEmpty ? (
                <FooterView
                  isLoading={isLoading && data.length > 0 && !isRefreshing}
                  total={data.length}
                  error={error}
                  hasMore={hasMore}
                  onRetry={onRetry}
                  space={safeAreaInsets.bottom}
                />
              ) : null)
            }
            css={`
              flex: 1;
              padding-top: ${rpx(8)}px;
            `}
          />
        </ReportViewSummaryProvider>
      );
    },
  ),
);
